import { useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Clock, Dumbbell } from "lucide-react"
import type { Appointment } from "../../../types"
import type { CalendarRoutine } from "../../../api/appointments"
import AppointmentCard from "./AppointmentCard"

const DAY_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"]

function getWeekDays(date: Date): Date[] {
  const d = new Date(date)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day // Monday start
  d.setDate(d.getDate() + diff)
  return Array.from({ length: 7 }, (_, i) => {
    const r = new Date(d)
    r.setDate(d.getDate() + i)
    return r
  })
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function formatTime(iso: string) {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleTimeString("es-CR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
}

export default function WeekView({
  currentDate,
  appointments,
  routines = [],
  onSelectAppointment,
  onDropAppointment,
}: {
  currentDate: Date
  appointments: Appointment[]
  routines?: CalendarRoutine[]
  onSelectAppointment: (a: Appointment) => void
  onDropAppointment?: (appointmentId: number, targetDate: Date, hour: number) => void
}) {
  const weekDays = getWeekDays(currentDate)
  const today = new Date()
  const [dragOverDay, setDragOverDay] = useState<number | null>(null)

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }

  function handleDrop(e: React.DragEvent, day: Date) {
    e.preventDefault()
    setDragOverDay(null)
    const idStr = e.dataTransfer.getData("application/appointment-id")
    if (idStr && onDropAppointment) {
      onDropAppointment(Number(idStr), day, 8) // default to 8am when dropping on day
    }
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentDate.toISOString()}
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -30 }}
        transition={{ duration: 0.2 }}
        className="overflow-x-auto"
      >
        <div className="min-w-[600px] grid grid-cols-7 gap-1">
          {weekDays.map((day, i) => {
            const isToday = isSameDay(day, today)
            const isDragOver = dragOverDay === i

            // Get all appointments for this day, sorted by start time
            const dayAppts = appointments
              .filter((a) => {
                const start = new Date(a.start_time)
                return !isNaN(start.getTime()) && isSameDay(start, day)
              })
              .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())

            return (
              <div
                key={i}
                onDragOver={handleDragOver}
                onDragEnter={() => setDragOverDay(i)}
                onDragLeave={() => setDragOverDay((prev) => prev === i ? null : prev)}
                onDrop={(e) => handleDrop(e, day)}
                className={`rounded-lg border transition-colors ${
                  isDragOver
                    ? "border-primary/60 bg-primary/10"
                    : isToday
                      ? "border-primary/40 bg-primary/5"
                      : "border-white/5 bg-white/[0.02]"
                }`}
              >
                {/* Day header */}
                <div className={`text-center py-2 border-b border-white/5`}>
                  <p className="text-xs text-muted">{DAY_LABELS[i]}</p>
                  <p className={`text-sm font-semibold ${isToday ? "text-primary" : "text-white"}`}>
                    {day.getDate()}
                  </p>
                </div>

                {/* Appointments + Routines */}
                <div className="p-1 space-y-1 min-h-[120px]">
                  {dayAppts.map((appt) => (
                    <div key={appt.id}>
                      <p className="text-[9px] text-muted flex items-center gap-0.5 px-1 pt-0.5">
                        <Clock size={8} />
                        {formatTime(appt.start_time)}
                      </p>
                      <AppointmentCard
                        appointment={appt}
                        compact
                        onClick={() => onSelectAppointment(appt)}
                      />
                    </div>
                  ))}
                  {/* Routines for this day */}
                  {routines
                    .filter((r) => r.scheduled_day === day.getDay())
                    .sort((a, b) => a.start_hour - b.start_hour)
                    .map((r) => (
                      <div key={`r-${r.id}`}>
                        <p className="text-[9px] text-muted flex items-center gap-0.5 px-1 pt-0.5">
                          <Dumbbell size={8} />
                          {`${String(r.start_hour).padStart(2, "0")}:00–${String(r.end_hour).padStart(2, "0")}:00`}
                        </p>
                        <div className="w-full rounded-lg border border-violet-400/40 bg-violet-400/10 p-1.5">
                          <div className="flex items-start gap-1">
                            <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-violet-400" />
                            <p className="text-[10px] font-semibold text-violet-400 truncate">
                              {r.user?.name || "Sin usuario"}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
